import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { BaseListing } from '../../../pages/_base-listing/base-listing'
import { CoreService, NotificationService } from '../../../services'
import { Category, Depot, Product, Supplier, Vat } from 'depoto-core/src/entities'
import { CategoryTreeType } from '../../../models'
import { TranslateService } from '@ngx-translate/core'

const optionKeepCurrent = 'keep.current'
const optionKeepCurrentSetSelected = 'keep.current.set.selected'

interface Schema {
  id: null
  name?: null
  categories?: {
    id: null
  }
  mainCategory?: {
    id: null
  }
  vat?: {
    id: null
  }
  purchasePrice?: null
  supplier?: {
    id: null
  }
}

@Component({
  selector: 'modal-bulk-edits',
  templateUrl: './modal-bulk-edits.component.html',
  styleUrls: ['./modal-bulk-edits.component.scss'],
})
export class ModalBulkEditsComponent extends BaseListing {
  public categoryTreeTypeEnum = CategoryTreeType
  public editedCategory: Category
  @ViewChild('modal') childModal: ModalContainerComponent
  @Output() refreshProductsPage: EventEmitter<any> = new EventEmitter<any>()
  @Output() setCheckedProductsToFalse: EventEmitter<any> = new EventEmitter<any>()
  @Input() filters: any = {}
  @Input() checkedProducts = []
  @Input() allProductsSelected = false

  vats: Array<Vat | string> = [optionKeepCurrent]
  categories: Category[] = []
  depots: Depot[] = []

  categorySelectedOption = optionKeepCurrentSetSelected
  mainCategorySelectedOption = optionKeepCurrent
  mainCategorySelectOptions = [optionKeepCurrent, 'set.selected', 'remove.current']

  selectOptions = ['keep.current.set.selected', 'keep.current.remove.selected', 'remove.current.add.selected']

  booleanSelectOptions = [optionKeepCurrent, 'yes', 'no']

  suppliers: Array<Supplier | string> = [optionKeepCurrent]

  purchasePrice: any

  productChanges: Partial<Product> | any = {
    categories: [],
    mainCategory: null,
    purchasePrice: null,
    supplier: optionKeepCurrent,
    vat: optionKeepCurrent,
  }

  productsFetched: Partial<Product>[] | any = []

  loading = false

  numberOfAllProducts: number
  numberOfUpdatedProducts: number
  isShowingProgress = false
  isShowingCloseBtn = false
  updateErrors = []
  wasKilled = false
  updating = false
  productsWithErrors = []
  isProgressFinish = false

  constructor(
    protected core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {
    super(core)
  }

  async showChildModal() {
    this.loading = true
    if (!this.allProductsSelected && this.checkedProducts?.length < 1) {
      alert(this.translateService.instant('modal-bulk-edits.select-product'))
      return
    }
    if (this.vats?.length === 0 || (this.vats?.length === 1 && this.vats[0] === optionKeepCurrent))
      await this.getOptions()
    this.setDefaultValues(true)
    this.loading = false
    this.childModal.showChildModal()
  }

  hideChildModal() {
    this.childModal.hideChildModal()
  }

  get isModalShown(): boolean {
    return this.childModal?.isShown() || false
  }

  async getOptions() {
    this.categories = this.core.lists.categories
    this.suppliers = [...this.suppliers, ...this.core.lists.suppliers]
    this.vats = [...this.vats, ...this.core.lists.vats]
  }

  setDefaultProductChanges() {
    this.productChanges = {
      categories: [],
      mainCategory: null,
      purchasePrice: null,
      supplier: optionKeepCurrent,
      vat: optionKeepCurrent,
    }
  }

  close() {
    if (!this.isProgressFinish) {
      if (confirm(this.translateService.instant('modal-bulk-edits.terminate-action'))) {
        this.wasKilled = true
      } else return
    }
    this.setDefaultValues()
    this.hideChildModal()
  }

  setDefaultValues(isShowingModal = false): void {
    this.setDefaultProductChanges()
    this.categorySelectedOption = optionKeepCurrentSetSelected
    this.mainCategorySelectedOption = optionKeepCurrent
    this.productsFetched = []
    this.numberOfAllProducts = undefined
    this.numberOfUpdatedProducts = undefined
    this.updateErrors = []
    this.productsWithErrors = []

    this.isShowingProgress = false
    this.isShowingCloseBtn = false
    this.updating = false
    this.isProgressFinish = false
    this.wasKilled = false
    if (!isShowingModal) {
      this.refreshProductsPage.emit()
      this.setCheckedProductsToFalse.emit()
    }
  }

  mainCategoryChanged(event) {
    this.productChanges.mainCategory = event.id
  }

  suspend() {
    this.wasKilled = true
  }

  async updateProduct(product, changedProperties, schemaForUpdate) {
    if (this.wasKilled) {
      this.setDefaultValues()
      this.hideChildModal()
      return
    }

    product = { id: product.id, ...changedProperties }

    if (this.productChanges.categories?.length) {
      await this.fetchCategoriesOnProduct(product)
      await this.setCategoriesOnProductToUpdate(product)
    }

    try {
      await this.update(product, schemaForUpdate)
    } catch (err) {
      let errMessage
      if (Object.prototype.toString.call(err[0]) === '[object Array]') {
        for (const object of err) {
          for (const key in object) {
            if (object.hasOwnProperty(key)) {
              errMessage = object[key]
            }
          }
        }
      } else errMessage = err[0]

      this.updateErrors.push({ productId: product.id, productName: product?.name, errMessage: errMessage })
    } finally {
      this.numberOfUpdatedProducts = this.numberOfUpdatedProducts + 1
      await this.core.services.product.getById(product.id, schemaForUpdate)
    }
  }

  async save() {
    this.loading = true
    this.numberOfUpdatedProducts = 0
    let page = 1
    let endPage: number
    const schema: Schema = { id: null, name: null }
    const schemaForUpdate: Schema = { id: null }
    if (this.productChanges.categories?.length) schema.categories = { id: null }

    //object with changed properties from productChanges
    const changedProperties = {}
    if (this.mainCategorySelectedOption === 'remove.current') {
      this.productChanges.mainCategory = null
      changedProperties['mainCategory'] = null
      schemaForUpdate['mainCategory'] = { id: null }
    }
    const entries = Object.entries(this.productChanges)
    for (const [key, value] of entries) {
      if (value !== 'keep.current' && value !== null && key !== 'categories') {
        if (['vat', 'supplier', 'mainCategory'].includes(key)) {
          schemaForUpdate[key] = { id: null }
          changedProperties[key] = { id: value }
        } else {
          changedProperties[key] = value
        }
      }
    }

    //for avoid error alerts while update
    this.notificationService.avoidErrorAlerts = true

    if (this.allProductsSelected) {
      do {
        if (this.wasKilled) {
          this.setDefaultValues()
          this.hideChildModal()
          return
        }
        //prepare products to update
        const { items, paginator } = await this.core.services.product.getList(
          { page: page, sort: 'name', direction: 'asc', filters: this.filters },
          schema,
        )
        this.numberOfAllProducts = paginator.totalCount
        this.loading = false
        this.isShowingProgress = true
        this.updating = true
        endPage = paginator.endPage
        page = page + 1

        //loop and update every product
        await Promise.all(items.map(product => this.updateProduct(product, changedProperties, schemaForUpdate)))
      } while (page <= endPage)
    } else {
      this.numberOfAllProducts = this.checkedProducts?.length
      this.loading = false
      this.isShowingProgress = true
      this.updating = true
      await Promise.all(
        this.checkedProducts.map(product => this.updateProduct(product, changedProperties, schemaForUpdate)),
      )
    }

    this.isProgressFinish = true
    //allow err alerts
    this.notificationService.avoidErrorAlerts = false

    // hide suspend button, show close btn
    this.updating = false
    this.isShowingCloseBtn = true
  }

  async update(product, schema) {
    await this.core.services.product.updateProductPart(product, schema)
  }

  async fetchCategoriesOnProduct(product) {
    const productWithCategories = await this.core.services.product.getById(product.id, {
      categories: {
        id: null,
        externalId: null,
        name: null,
      },
    })
    product.categories = productWithCategories.categories
  }

  setCategoriesOnProductToUpdate(product) {
    product.categories = []
    if (this.categorySelectedOption === 'keep.current.set.selected') {
      if (product.categories?.length > 0) {
        this.productChanges.categories.filter(cat => {
          if (!product.categories.find(c => c.id === cat.id)) product.categories.push(cat)
        })
      } else product.categories = this.productChanges.categories
    } else if (this.categorySelectedOption === 'keep.current.remove.selected') {
      for (const changedCategory of this.productChanges.categories) {
        if (product.categories.some(cat => cat.id === changedCategory.id))
          product.categories = product.categories.filter(category => category.id !== changedCategory.id)
      }
    } else if (this.categorySelectedOption === 'remove.current.add.selected') {
      product.categories = this.productChanges.categories
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  filter(pageNo: number, sortProp: string, sortDir: string, concat: boolean): void {}
}
